/**
 * Dragging in the constructor: where a drop lands, and what it carries
 * (DESIGN §9.3, D12).
 *
 * Native HTML5 drag events, no library — the same reason arrow keys reorder
 * the list: `package.json` has five runtime dependencies on purpose, and a
 * drag-and-drop library is the first thing everyone reaches for.
 *
 * **A design is a vertical stack, not a canvas.** There is no x/y here and
 * there never will be: one document has to lay itself out on every phone the
 * customer's fleet happens to own, so a drop can only ever answer "between
 * which two blocks?". Where a block sits across the screen is `align` and
 * `fullWidth`, and where the stack sits down it is the theme's
 * `verticalAlign` — which is what the hint under the block list says.
 */

import { BLOCK_TYPES, MODULE_ID_RE, type BlockType } from "./spec";

/** The drag payload for reordering: a block id, in our own MIME type so a
 *  picture dragged out of another tab is never mistaken for one. */
export const BLOCK_MIME = "application/x-qa-block";

/**
 * The palette's payload: what a tile *makes*, not a block id. A second MIME
 * rather than a flag inside the first, because `dragover` can only ask what
 * types a drag carries — so "this will move the block you are holding" and
 * "this will make a new one" have to be distinguishable before the drop.
 */
export const BLOCK_TYPE_MIME = "application/x-qa-block-type";

/**
 * What a palette tile makes: one of the core block types, or a block a module
 * contributes (MODULES §7.4). Both ride the one MIME type, because both
 * answer the same question at the same moment.
 */
export type PaletteItem =
  | { kind: "block"; type: BlockType }
  | { kind: "module"; module: string; block: string };

/** `divider`, or `module:webwrapper:page` — ids hold no colon, so neither can
 *  be mistaken for the other. */
export function paletteValue(item: PaletteItem): string {
  return item.kind === "block" ? item.type : `module:${item.module}:${item.block}`;
}

/**
 * Where a drop lands. `insert` names a slot *between* blocks — 0 is above the
 * first, `blocks.length` is below the last — and `replace` names an image
 * block the pointer is actually over, whose picture is swapped instead.
 */
export type DropTarget = { kind: "insert"; index: number } | { kind: "replace"; id: string };

/**
 * The slot the pointer is in, by each row's midpoint: above a row's middle is
 * the slot before it, below the last row's middle is the end of the stack.
 */
export function slotAt(rows: readonly HTMLElement[], clientY: number): number {
  for (const [index, row] of rows.entries()) {
    const box = row.getBoundingClientRect();
    if (clientY < box.top + box.height / 2) return index;
  }
  return rows.length;
}

/** Is this drag carrying files from the desktop rather than one of our rows? */
export function hasFiles(transfer: DataTransfer | null | undefined): boolean {
  return Array.from(transfer?.types ?? []).includes("Files");
}

/** The dropped files, as a plain array — `DataTransfer.files` is a FileList. */
export function filesOf(transfer: DataTransfer | null | undefined): File[] {
  return Array.from(transfer?.files ?? []);
}

/**
 * Is this drag one of our rows? While a drag is *over* something the payload
 * itself is unreadable — the browser hands out only its types until the drop —
 * so this is what a `dragover` handler has to ask.
 */
export function carriesBlock(transfer: DataTransfer | null | undefined): boolean {
  return Array.from(transfer?.types ?? []).includes(BLOCK_MIME);
}

/** The block id a reorder drag carries, readable on `drop` and not before. */
export function draggedBlock(transfer: DataTransfer | null | undefined): string | null {
  return transfer?.getData?.(BLOCK_MIME) || null;
}

/** Is this drag a tile from the palette? Asked by `dragover`, like the above. */
export function carriesBlockType(transfer: DataTransfer | null | undefined): boolean {
  return Array.from(transfer?.types ?? []).includes(BLOCK_TYPE_MIME);
}

/**
 * What a palette drag carries. Checked rather than trusted: this arrives as a
 * string from a drag the page did not necessarily start, so a core type has to
 * be in the closed list and a module reference has to be shaped like one. That
 * the module is in *this* release is the validator's question, not this one's:
 * a tile can only have come from a catalogue the page rendered.
 */
export function draggedPaletteItem(
  transfer: DataTransfer | null | undefined,
): PaletteItem | null {
  const value = transfer?.getData?.(BLOCK_TYPE_MIME) || "";
  if ((BLOCK_TYPES as readonly string[]).includes(value)) {
    return { kind: "block", type: value as BlockType };
  }
  const [prefix, module, block, ...rest] = value.split(":");
  if (prefix !== "module" || rest.length > 0) return null;
  if (!MODULE_ID_RE.test(module ?? "") || !MODULE_ID_RE.test(block ?? "")) return null;
  return { kind: "module", module, block };
}
